import { useCallback, useEffect, useRef } from 'react'
import { useAtom } from 'jotai'
import { useDeleteAudioNote } from '@renderer/hooks/audio-notes'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useIsAudioNoteStartPending } from '@renderer/hooks/audio/start-attempt'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useViewContext, useViewEntranceSettled } from '../../view-context'
import { useViewActions } from '../../view-atoms'
import { EmptyState, EmptyStateDescription, EmptyStateIcon } from '../../components/meeting/empty-state'
import { View } from '../../components/view'
import { DeleteConfirmation } from '../../components/meeting/delete-confirmation'
import { MeetingViewHeader } from './nested/header'
import { MeetingViewLiveHeader } from './nested/live-header'
import { useSidepanelSurface } from '../../profile/use-sidepanel-profile'
import { MeetingViewBody } from './nested/body'
import { MeetingViewHoursPaywall } from './nested/meeting-hours-paywall'
import { MeetingChatDrawer } from './nested/chat-drawer'
import { meetingDeletePendingAtom } from './atoms'
import { deriveMeetingViewPresence } from './meeting-view-presence'
import {
  useMeetingContextLatch,
  useMeetingViewModel,
  useResolvedMeetingNoteRefresh,
  type MeetingViewContext,
} from './use-meeting-view-model'
import { useMeetingChatAttachment } from './use-meeting-chat-attachment'
import { meetingNotepadKey } from './notepad-key'
import { useMeetingNotepadClaim } from './use-meeting-notepad-claim'
import { useResolvedMeetingChatId } from './use-meeting-chat-drawer'
import { useMeetingChatThread } from './use-meeting-chat-thread'
import { MeetingTour } from '../../components/meeting/meeting-tour'
import { hasMeetingTabs } from './meeting-tabs'
import { FileText } from 'lucide-react'

/**
 * Meeting View (HIG-5823): the single drill-in View for a meeting across its
 * whole lifecycle — live transcription, past notes, and Custom Meetings — as
 * one component with presentational branches, replacing the two legacy
 * drawers as the meeting surface.
 */
export function MeetingView() {
  const { context } = useViewContext<MeetingViewContext>()
  const model = useMeetingViewModel(context)
  const layout = useSidepanelSurface('meetingView')
  const isLive = model.variant === 'live'
  const isTabbed = layout === 'tabs'
  // The redesigned sidebar's recorded meeting is just its action items and transcript, untitled
  const hidesHeader = layout === 'one-column' && hasMeetingTabs(model)
  useMeetingContextLatch(context, model)
  useResolvedMeetingNoteRefresh(model.audioNoteId)
  useMeetingChatAttachment(model)
  useMeetingNotepadClaim(isTabbed ? undefined : meetingNotepadKey(model))
  useMeetingChatThread(context, model, { enabled: isTabbed })
  const chatId = useResolvedMeetingChatId(context?.chatId)
  // Like the Chat View, the drawer's list waits for the slide-in to settle —
  // rendering markdown for a whole thread would block the first frames.
  const entranceSettled = useViewEntranceSettled()

  // Latched for this mount: once the recording has been seen, the entry going
  // quiet again means it ended, not that it hasn't begun. The latched note id
  // counts too — it only exists because a session did.
  const sessionSeen = useRef(false)
  if (model.variant === 'live' || context?.audioNoteId) sessionSeen.current = true
  const startPending = useIsAudioNoteStartPending()
  const presence = deriveMeetingViewPresence({
    mode: context?.mode ?? 'note',
    variant: model.variant,
    isLoading: model.isLoading,
    hasAudioNote: !!model.audioNote,
    hasCalendarEvent: !!model.calendarEvent,
    sessionSeen: sessionSeen.current,
    startPending,
  })
  const { closeView } = useViewActions()
  // A live entry with no recording to follow — discarded, or never started —
  // has nothing to show and never will: leave the way a deleted meeting does.
  useEffect(() => {
    if (presence === 'discarded') closeView()
  }, [presence, closeView])

  if (presence === 'loading' || presence === 'pending' || presence === 'discarded') {
    return (
      <View>
        <View.Skeleton />
      </View>
    )
  }

  if (presence === 'not-found') {
    return (
      <View>
        <div className="flex flex-1 flex-col items-center justify-center">
          <EmptyState>
            <EmptyStateIcon>
              <FileText />
            </EmptyStateIcon>
            <EmptyStateDescription>Meeting not found</EmptyStateDescription>
          </EmptyState>
        </div>
      </View>
    )
  }

  return (
    <View>
      {isTabbed && isLive ? (
        <MeetingViewLiveHeader model={model} />
      ) : (
        !hidesHeader && <MeetingViewHeader model={model} />
      )}
      <MeetingViewBody model={model} layout={layout} />
      <MeetingViewHoursPaywall model={model} />
      <MeetingDeleteConfirmation audioNoteId={model.audioNote?.id} discardsRecording={isTabbed && isLive} />
      {/* Mounted (closed) for as long as a thread exists, so both the open and
          the dismiss transition, and a dismissed thread can be resumed from the
          composer's "Resume chat" row. A meeting with a Chat tab holds the
          thread there instead. */}
      {chatId && (
        <MeetingChatDrawer
          chatId={chatId}
          open={!(isTabbed && hasMeetingTabs(model)) && Boolean(context?.chat)}
          entranceSettled={entranceSettled}
          isFreshSubmit={context?.isFreshSubmit}
        />
      )}
      {/* Mounted here like the delete confirmation: its prompt card positions
          against the whole view surface. Held while the one-column layout hides the header, tabs and recap it walks. */}
      {!hidesHeader && <MeetingTour model={model} />}
    </View>
  )
}

/**
 * Triggered from the floating bar or the live header's menu, but mounted here:
 * the dialog scrims its positioned parent, and in the floating bar that's only
 * the ~40px control row. From the live header it discards the recording instead.
 */
function MeetingDeleteConfirmation({
  audioNoteId,
  discardsRecording,
}: {
  audioNoteId?: string
  discardsRecording: boolean
}) {
  const [isPending, setPending] = useAtom(meetingDeletePendingAtom)
  const { mutateAsync: deleteAudioNote } = useDeleteAudioNote()
  const { closeView } = useViewActions()

  const handleCancel = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setPending(false)
    },
    [setPending],
  )

  const handleConfirm = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      setPending(false)
      if (discardsRecording) {
        AudioNoteIpcRenderer.stopRecording(false)
        AudioNoteIpcRenderer.clearAudioNoteState()
        closeView()
        AmplitudeRenderer.trackEvent('Audio Note Action', { action: 'discard', trigger: 'sidepanel meeting view' })
        return
      }
      if (!audioNoteId) return
      try {
        await deleteAudioNote(audioNoteId)
        closeView()
      } catch (error) {
        console.error('Failed to delete meeting:', error)
        return
      }
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'delete',
        trigger: 'sidepanel meeting view',
      })
    },
    [audioNoteId, discardsRecording, deleteAudioNote, closeView, setPending],
  )

  return (
    <DeleteConfirmation
      isOpen={isPending}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmationText={
        discardsRecording
          ? "Discard this recording? Its transcript won't be saved."
          : 'Are you sure you want to delete this meeting?'
      }
      confirmLabel={discardsRecording ? 'Discard' : 'Delete'}
    />
  )
}
